import type {RouteLocationRaw} from 'vue-router';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {
  type TodayCardItem,
  TodayCardKind,
  type TodayNextTask,
  type TodayTask,
  type TodayUpcoming,
  TodayUpcomingFace,
} from '@/ts/types/component/today-page';
import {
  type NextTaskDTO,
  type TodayTaskDTO,
  TodayTaskTypeDTO,
  type UpcomingTileDTO,
  UpcomingTileKindDTO,
} from '@/ts/types/dto/today-dto';
import {pagesPath} from '@/ts/router/pages-path';

/**
 * Block content is reached by route name rather than through `pagesPath`: its path is registered
 * as a child of the main route and carries no leading slash, so it cannot be linked to by path.
 */
const BLOCK_CONTENT_ROUTE_NAME = 'block-content';

/**
 * Fills a route template's `:params`. The router prefixes the language itself, so the mapper stays
 * pure — the existing `getVueLink` reads the current URL and stored preferences, which a pure
 * function may not do.
 */
function buildPath(template: string, params: Record<string, string>): string {
  // The `?.`/`??` pair is unreachable at runtime — every caller passes a member of a frozen route
  // enum — but sonarjs/null-dereference cannot prove that and fails the plain call. Analyzer
  // appeasement, not defence; verified by removing it.
  return template?.replaceAll(
    /:(?<name>\w+)/gu,
    (placeholder, name: string) => params[name] ?? placeholder,
  ) ?? template;
}

/**
 * The 1-based page position the session CTA opens. Anything absent, zero or negative falls back to
 * the first page. Only the lower bound: `TodaySessionDTO` carries no page count, so the FE cannot
 * check the upper one, and a position past the last page reaches a 404 the same way it would have
 * before. A fractional value also passes through, and the route's `parseInt` truncates it.
 */
function toPageNumber(openPageNumber: number | null | undefined): number {
  return openPageNumber && openPageNumber > 0 ? openPageNumber : 1;
}

/**
 * Whether a task may open the session itself, at the page the reader left off.
 *
 * On: the Quality Management Representative has stated that a shortcut may take someone into a
 * programme they have started (IPD-2980, subtask 14.1). The switch stays for the day that is
 * revisited — off, a session task opens the programme's landing page instead, one click from the
 * same place, and no page of a session is entered from here.
 *
 * Off also overrides the page-1 fallback above, since that lands inside the session too. It is the
 * default of `mapTodayTaskItem`'s second parameter rather than a value read inside it, so a test
 * drives either answer through the real mapper and neither branch can rot unnoticed.
 */
const MAY_OPEN_SESSION_PAGE = true;

/**
 * Turns the one suggestion the payload carries into a card. Exactly one of the three fields is
 * set; null when none is, which sends the tile to its idle face.
 *
 * None of the three shows a progress bar: a suggestion is an invitation to start, and the
 * "Awesome!" sheet draws no bar on any of its three variants.
 */
function mapRecommendation(dto: UpcomingTileDTO): TodayCardItem | null {
  if (dto.intervention) {
    return {
      id: `program:${dto.intervention.id}`,
      kind: TodayCardKind.program,
      title: dto.intervention.name,
      parentName: null,
      imageUrl: dto.intervention.image,
      progress: null,
      done: false,
      to: buildPath(pagesPath.intervention.interventionLanding, {iid: dto.intervention.id}),
    };
  }
  if (dto.challenge) {
    return {
      id: `challenge:${dto.challenge.id}`,
      kind: TodayCardKind.challenge,
      title: dto.challenge.title,
      parentName: null,
      imageUrl: dto.challenge.imageUrl ?? null,
      progress: null,
      done: false,
      to: {name: pagesPath.content.challenge, params: {id: String(dto.challenge.id)}},
    };
  }
  // A content block's name is never null on the wire but it CAN be blank: the CMS allows a
  // nameless block for pure-media content and the backend serialises that as an empty string
  // (the mirror still types it nullable, so both are handled). A card with no title is not worth
  // showing, so it is no card — and the check must be on blankness, not truthiness alone, or a
  // whitespace-only name would render a silent card.
  const contentName = dto.content?.name?.trim();
  if (dto.content && contentName) {
    return {
      id: `content:${dto.content.id}`,
      kind: TodayCardKind.content,
      title: contentName,
      parentName: null,
      imageUrl: dto.content.image,
      progress: null,
      done: false,
      // `backLabel` is an i18n KEY that back-link resolves, falling back to a generic "Back" when
      // the state is absent. Every other way into block-content names its origin (the Explore
      // cards, the home band, the sidebar bookmarks), so a reader arriving from here should read
      // "Today" on the way out — `home` is the same key the page's own title uses.
      to: {
        name: BLOCK_CONTENT_ROUTE_NAME,
        params: {itemId: dto.content.id},
        state: {backLabel: 'home'},
      },
    };
  }
  return null;
}

/**
 * What the unlock date belongs to, when the backend sending it has reached this deployment.
 *
 * Absent, the face keeps the sentence it had before this existed: the date on its own. Present, it
 * is whole — the backend never sends a partial object — so the only judgement left here is the
 * title. A blank string is what the CMS yields for a session nobody named, so the programme's
 * name stands in for it; blankness and not truthiness, for the same reason the content block
 * above is checked that way — a whitespace-only name would otherwise name the session " ".
 *
 * Both names are trimmed, because the eyebrow is dropped by comparing them: an untrimmed parent
 * against a trimmed title would print the programme twice on exactly the payload the fallback
 * exists for.
 */
function mapNextTask(dto: NextTaskDTO | null | undefined): TodayNextTask | null {
  if (!dto) {
    return null;
  }

  const parentName = dto.name.trim();

  return {
    parentName,
    title: dto.sessionName.trim() || parentName,
    sessionNumber: dto.sessionNumber,
    totalSessions: dto.totalSessions,
  };
}

/**
 * Where a task that would enter a session leads — the session task at its resume page, the
 * homework at the first page of the session that holds it — as a function of that permission
 * rather than of the constant, so both answers stay covered by tests.
 *
 * On the session side: the route's `pid` is a 1-BASED POSITION everywhere in the app
 * (use-route-params parseInts it and use-page reads pages[pid - 1]), so the payload's `openPageId` —
 * a page UUID — cannot go in it. The resume point rides on `openPageNumber` instead, which the
 * backend derives from the same answer as the id, so the two cannot name different pages. Still
 * optional on the wire, and absent it opens the session at page 1, the same landing every other
 * link in the app uses. Validated rather than defaulted,
 * because this is the only producer of `pid` in the app that does not derive it structurally — the
 * nine sites in use-links and the rest pass a literal 1 or an `index + 1`. A 0 shipped by mistake
 * (exactly the off-by-one the field's name exists to close) or any other non-positive value would
 * reach intervention-session-page, which sends anything outside 1..pages.length to /404.
 */
export function sessionPageTarget(
  session: {interventionId: UuidDTO; sessionId: UuidDTO; openPageNumber?: number | null},
  mayOpenSessionPage: boolean,
): RouteLocationRaw {
  if (!mayOpenSessionPage) {
    return buildPath(pagesPath.intervention.interventionLanding, {iid: session.interventionId});
  }

  return buildPath(pagesPath.session.sessionPage, {
    iid: session.interventionId,
    sid: session.sessionId,
    pid: String(toPageNumber(session.openPageNumber)),
  });
}

/**
 * Flattens one task of the payload into the card the tiles render.
 *
 * The payload's task has no id and no flat fields: `type` says which of four sub-objects carries
 * the data. Ids are synthesised with a type prefix because the four id spaces are unrelated — a
 * challenge numbers its sessions from one, everything else uses UUIDs — and a bare id would
 * collide across kinds.
 *
 * Exported because the tasks tile (IPD-2977) renders the same card from the same sub-objects: the
 * list it shows opens with the very task this tile is showing, so the two must synthesise it
 * identically or they would describe the same task differently.
 *
 * Returns null when the sub-object the type promises is absent, which the payload's types allow
 * and no caller can render.
 */
export function mapTodayTaskItem(
  dto: TodayTaskDTO,
  mayOpenSessionPage: boolean = MAY_OPEN_SESSION_PAGE,
): TodayTask | null {
  switch (dto.type) {
    case TodayTaskTypeDTO.SESSION: {
      const {session} = dto;
      if (!session) {
        return null;
      }
      return {
        id: `session:${session.sessionId}`,
        kind: TodayCardKind.session,
        title: session.sessionName,
        parentName: session.interventionName,
        imageUrl: session.interventionImage,
        progress: session.sessionProgress,
        done: dto.done,
        to: sessionPageTarget(session, mayOpenSessionPage),
      };
    }
    case TodayTaskTypeDTO.HOMEWORK: {
      const {homework} = dto;
      if (!homework) {
        return null;
      }
      return {
        id: `homework:${homework.contentId}`,
        kind: TodayCardKind.homework,
        title: homework.title,
        // Homework has no descriptive title of its own — every intervention names the page
        // "Homework" — so the session it came from is the only thing that tells two apart.
        parentName: homework.sessionName,
        imageUrl: null,
        progress: null,
        done: dto.done,
        // Page 1 rather than the bare session route: `sessionWrapper` has no default child, so a
        // bare link renders an empty shell. Landing on the first page is what the app's own
        // homework links do (see useCurrentPageLink's HOMEWORK_PENDING case). Through the same
        // permission as the session task, since this enters a session page just as much: with it
        // off, homework stops at the programme like everything else here.
        to: sessionPageTarget({
          interventionId: homework.interventionId,
          sessionId: homework.sessionId,
          openPageNumber: 1,
        }, mayOpenSessionPage),
      };
    }
    case TodayTaskTypeDTO.CHALLENGE: {
      const {challenge} = dto;
      if (!challenge) {
        return null;
      }
      return {
        id: `challenge:${challenge.challengeId}:${challenge.sessionId}`,
        kind: TodayCardKind.challenge,
        title: challenge.sessionName,
        parentName: challenge.challengeName,
        // Challenges serve an absolute URL where everything else serves a Directus asset id;
        // the card accepts either.
        imageUrl: challenge.challengeImageUrl,
        progress: challenge.sessionProgress,
        done: dto.done,
        to: {
          name: pagesPath.content.challengeSession,
          params: {id: String(challenge.challengeId), sessionId: String(challenge.sessionId)},
        },
      };
    }
    case TodayTaskTypeDTO.ASSESSMENT: {
      const {questionnaire} = dto;
      if (!questionnaire) {
        return null;
      }
      return {
        id: `assessment:${questionnaire.id}`,
        kind: TodayCardKind.assessment,
        title: questionnaire.name,
        // A questionnaire belongs to nothing, so the card falls back to its kind label.
        parentName: null,
        imageUrl: questionnaire.image,
        progress: null,
        done: dto.done,
        // No route: questionnaires open in the existing modal, so the card reports activation.
        to: null,
      };
    }
    default: {
      // Exhaustiveness guard: a new task type from the backend fails this assignment at compile
      // time instead of being silently mapped as something else. At runtime an unknown type
      // yields no card, which the tile treats as "nothing to show".
      const unknownType: never = dto.type;
      void unknownType;
      return null;
    }
  }
}

/**
 * Translates the upcoming tile's slice of `GET /me/today` into what the tile renders.
 *
 * The payload is a tagged union flattened into one object: `kind` says which of the sibling
 * fields carries the data, and the rest arrive null. The view-model is a real discriminated
 * union, so most of the work here is turning "kind plus six nullable fields" into "one face with
 * exactly its own data" — and deciding what to do when the field the kind promises is missing.
 *
 * `now` is deliberately NOT a parameter, unlike the assessment mapper: nothing in this tile is
 * derived from the current instant. The greeting band is resolved at render time from the device
 * clock (`use-today-greeting`), and the unlock date crosses the wire raw for the tile to format.
 *
 * RECOMMENDATION carries the suggestion whoever the user is; `justOnboarded` decides only which
 * heading goes above it. The backend says so where it sets the flag ("so the frontend can
 * congratulate them rather than fall straight to the generic copy"), the rules doc says so in the
 * state whose rule is "link to a content recommendation if nothing else available", and the state
 * matrix says so in its S7 cell, "greeting + recommended content". So the flag picks the face, not
 * whether there is a card: gating the card on it would drop a suggestion the backend chose for
 * this user and replace it with the copy for having nothing to suggest — which the payload already
 * has its own way of saying, a null `kind`.
 */
export function mapTodayUpcoming(dto: UpcomingTileDTO): TodayUpcoming {
  switch (dto.kind) {
    case UpcomingTileKindDTO.ONBOARDING_CTA: {
      const {questionnaire} = dto;
      // The face exists to open this questionnaire; without it the CTA would lead nowhere.
      return questionnaire
        ? {
          face: TodayUpcomingFace.welcome,
          questionnaire: {id: questionnaire.id, name: questionnaire.name},
        }
        : {face: TodayUpcomingFace.idle};
    }
    case UpcomingTileKindDTO.TASK: {
      const task = dto.task ? mapTodayTaskItem(dto.task) : null;
      return task ? {face: TodayUpcomingFace.task, task} : {face: TodayUpcomingFace.idle};
    }
    case UpcomingTileKindDTO.NO_TASK_DUE: {
      // The whole face is a sentence about a date and the session that opens on it. Without either
      // there is nothing to promise, and the same rule answers both: a date with no session cannot
      // be written as a sentence any more than no date can.
      //
      // The backend cannot send one without the other — both come off a single `UpcomingWait`,
      // whose two fields are non-null — and a release ships both sides together, so what this
      // second half answers is the rollout itself, or a rollback that leaves this page ahead of
      // the backend. That user is told nothing is due today, which is true, rather than being
      // given a date belonging to nothing.
      const nextTask = mapNextTask(dto.nextTask);
      return dto.nextTaskDueDate && nextTask
        ? {
          face: TodayUpcomingFace.nothingDue,
          nextUnlockAt: dto.nextTaskDueDate,
          nextTask,
        }
        : {face: TodayUpcomingFace.idle};
    }
    case UpcomingTileKindDTO.RECOMMENDATION: {
      const recommendation = mapRecommendation(dto);
      // One payload, two faces, and the flag chooses between them rather than between showing the
      // card and hiding it. Without a card there is nothing to head either way, so that falls to
      // idle, whose copy is exactly "nothing to suggest".
      if (!recommendation) {
        return {face: TodayUpcomingFace.idle};
      }
      return dto.justOnboarded
        ? {face: TodayUpcomingFace.recommendation, recommendation}
        : {face: TodayUpcomingFace.suggestion, recommendation};
    }
    // `kind` is null for good: a licence whose recommendation packs are empty yields no task, no
    // next unlock and no suggestion, and the resolver falls through to an empty tile. Required
    // behaviour, not defence.
    case null: {
      return {face: TodayUpcomingFace.idle};
    }
    default: {
      // Exhaustiveness guard: a new kind from the backend fails this assignment at compile time
      // instead of silently rendering the idle face. At runtime it still falls back to idle.
      const unknownKind: never = dto.kind;
      void unknownKind;
      return {face: TodayUpcomingFace.idle};
    }
  }
}
